<script setup lang="ts">
import { Amplify } from 'aws-amplify'
import { onMounted, ref, watch } from 'vue'

import { mockAmplify } from '@/services/mockAmplify'
import { getAwsConfig } from '@/utils/aws-config'
import { getEnvBoolean } from '@/utils/env'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'
import { clearSubscriptionCache, closeWebSocket, openWebSocket } from '@/composables/useWebsocketValues'
import AuthenticatorCard from '@/elements/PreLogin/AuthenticatorCard.vue'
import LoginLayout from '@/layouts/LoginLayout.vue'
import HandleInviteCard from '@/elements/PreLogin/HandleInviteCard.vue'

const authStore = useAuthStore()
const isMockMode = getEnvBoolean('VITE_MOCK_AUTH')

const configLoaded = ref(false)
const configError = ref<string | null>(null)

const { isAuthenticated } = storeToRefs(authStore)

async function configureAmplify() {
  if (isMockMode) {
    await mockAmplify.configure({
      region: 'mock-region',
      Auth: { Cognito: { userPoolId: 'mock', userPoolClientId: 'mock' } },
    })
    configLoaded.value = true
    await authStore.initializeAuth()
    return
  }

  try {
    const awsConfig = getAwsConfig()
    Amplify.configure(awsConfig)
    console.log('configuring amplify')
    configLoaded.value = true
    await authStore.initializeAuth()
  } catch (error) {
    console.error('Failed to initialise Amplify with remote config', error)
    configError.value = error instanceof Error ? error.message : 'Unknown configuration error'
  }
}

onMounted(() => {
  configureAmplify()
})

watch(isAuthenticated, (authenticated, oldAuthenticated) => {
  if (authenticated) {
    openWebSocket()
  } else {
    closeWebSocket()
  }

  if (oldAuthenticated && !authenticated) {
    clearSubscriptionCache()
  }
})
</script>

<template>
  <div v-if="!isAuthenticated || $route.meta.isPreLogin" class="auth-wrapper">
    <div v-if="configError && !configLoaded" class="state-card state-card--error">
      <h2>Configuration error</h2>
      <p>Failed to load application configuration: {{ configError }}</p>
      <p>Please verify that infrastructure stacks are deployed and configuration files are available.</p>
      <button class="primary-btn" type="button" @click="configureAmplify">Retry</button>
    </div>

    <div v-else-if="!configLoaded" class="state-card">
      <div class="loader"></div>
      <p>Preparing your workspace…</p>
    </div>

    <LoginLayout v-else>
      <RouterView v-if="$route.meta.isPreLogin" v-slot="{ Component }">
        <component :is="Component" />
      </RouterView>
      <AuthenticatorCard v-else />
    </LoginLayout>
  </div>
  <slot v-else></slot>
</template>

<style scoped>
.auth-wrapper {
  width: 100%;
  height: 100%;
}

.state-card {
  width: min(420px, 100%);
  background: #fff;
  border-radius: 16px;
  padding: 2.25rem 2rem;
  display: grid;
  gap: 1rem;
  box-shadow: 0 28px 60px rgba(15, 23, 42, 0.18);
  text-align: center;
}

.state-card--error {
  border: 1px solid rgba(239, 68, 68, 0.35);
}

.loader {
  width: 56px;
  height: 56px;
  border: 4px solid rgba(59, 130, 246, 0.15);
  border-top-color: #2563eb;
  border-radius: 50%;
  margin: 0 auto;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
